// Thin client for `/exporter/delivery-forms/:id/*`. Currently the only
// endpoint is the per-delivery traceability roll-up; more will live
// here as the reception side of the portal grows.

import { api } from "@/lib/api/client"
import type { OTTraceabilityGeo } from "@/features/transit/api/types"
import type { BaseResponse } from "@/types/api"

import type { DeliveryFormDocument, DeliveryFormTraceability } from "./types"

const BASE = "/exporter/delivery-forms"

function unwrap<T>(data: BaseResponse<T>, fallback: string): T {
  if (!data.success) {
    throw new Error(data.message ?? fallback)
  }
  return data.result as T
}

export async function getDeliveryFormTraceabilityGeo(
  deliveryFormId: number | string,
): Promise<OTTraceabilityGeo> {
  const { data } = await api.get<BaseResponse<OTTraceabilityGeo>>(
    `${BASE}/${deliveryFormId}/traceability/geo`,
  )
  const result = unwrap(data, "Could not load parcel geometry")
  return { parcels: result.parcels ?? [] }
}

export async function getDeliveryFormDocuments(
  deliveryFormId: number | string,
): Promise<DeliveryFormDocument[]> {
  const { data } = await api.get<
    BaseResponse<{ documents: DeliveryFormDocument[] }>
  >(`${BASE}/${deliveryFormId}/documents`)
  const result = unwrap(data, "Could not load delivery form documents")
  return result.documents ?? []
}

export async function getDeliveryFormTraceability(
  deliveryFormId: number | string,
): Promise<DeliveryFormTraceability> {
  const { data } = await api.get<BaseResponse<DeliveryFormTraceability>>(
    `${BASE}/${deliveryFormId}/traceability`,
  )
  const result = unwrap(data, "Could not load delivery form traceability")
  // pq.StringArray can serialise to null for empty arrays. Same boundary
  // coercion as the OT-level traceability fetcher.
  const trace = result.traceability
  return {
    ...result,
    traceability: {
      ...trace,
      producers: (trace.producers ?? []).map((p) => ({
        ...p,
        certificates: p.certificates ?? [],
      })),
      parcels: (trace.parcels ?? []).map((p) => ({
        ...p,
        certifications: p.certifications ?? [],
      })),
      transactions: (trace.transactions ?? []).map((tx) => ({
        ...tx,
        certificates: tx.certificates ?? [],
      })),
    },
  }
}
